import type { HarmonyPackageBoardColumn } from '@rhythmic/db/types'
import type { CreateKanbanColumnPayload, KanbanContext } from '@rhythmic/types'
import { CreateKanbanColumnSchema, KanbanContextSchema } from '@rhythmic/types'
import { useRhythmicLogger } from '#imports'

import { getColumnsCollection } from '../getCollections'

const logger = useRhythmicLogger('useKanbanBoardContext.createColumn')

function validateParams(payload: CreateKanbanColumnPayload, context: KanbanContext | undefined) {
  if (!context) {
    logger.error('Attempted to create column without an active kanban context')
    throw new Error('Kanban board context is not ready: missing context for creating a column')
  }

  logger.debug('Validating parameters for handleCreateColumn', {
    payload,
    context,
  })

  const parsedContext = KanbanContextSchema.parse(context)
  const parsedPayload = CreateKanbanColumnSchema.parse({
    ...payload,
    boardId: payload.boardId ?? parsedContext.boardId,
    harmonyPackageId: payload.harmonyPackageId ?? parsedContext.packageId,
    workspaceId: payload.workspaceId ?? parsedContext.workspaceId,
  })

  if (parsedPayload.boardId !== parsedContext.boardId) {
    logger.error('Column payload board identifier mismatch', {
      payloadBoardId: parsedPayload.boardId,
      contextBoardId: parsedContext.boardId,
    })
    throw new Error('Board identifier in payload does not match current context')
  }

  if (parsedPayload.harmonyPackageId !== parsedContext.packageId) {
    logger.error('Column payload package identifier mismatch', {
      payloadPackageId: parsedPayload.harmonyPackageId,
      contextPackageId: parsedContext.packageId,
    })
    throw new Error('Package identifier in payload does not match current context')
  }

  return {
    context: parsedContext,
    payload: parsedPayload,
  }
}

export default function handleCreateColumn(payload: CreateKanbanColumnPayload, context: KanbanContext | undefined) {
  const { context: validatedContext, payload: validatedPayload } = validateParams(payload, context)
  const columnsCollection = getColumnsCollection(validatedContext.boardId)
  const insertPayload: Partial<HarmonyPackageBoardColumn> = {
    id: validatedPayload.id,
    name: validatedPayload.name,
    status_type: validatedPayload.statusType,
    position: validatedPayload.position,
    board_id: validatedContext.boardId,
    workspace_id: validatedPayload.workspaceId ?? validatedContext.workspaceId,
  }

  logger.debug('Inserting harmony package board column into collection', {
    boardId: validatedContext.boardId,
    columnId: insertPayload.id,
    position: insertPayload.position,
  })

  const insertResult = columnsCollection.insert(insertPayload as HarmonyPackageBoardColumn)
  const createdColumn = Array.isArray(insertResult) ? insertResult[0] : insertResult

  logger.info('Kanban column created successfully', {
    boardId: validatedContext.boardId,
    columnId: createdColumn?.id ?? insertPayload.id,
    position: insertPayload.position,
  })

  return createdColumn ?? insertPayload
}
